import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { AlertTriangle, Trash2, Merge, RefreshCw, Users, TestTube, CheckCircle, Loader2, Calendar, Link2Off, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const DataCleanupTab = () => {
  const [loading, setLoading] = useState(true);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [testRecords, setTestRecords] = useState([]);
  const [orphanBookings, setOrphanBookings] = useState([]);
  const [orphanEnrollments, setOrphanEnrollments] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState({});
  const [selectedTests, setSelectedTests] = useState([]);
  const [selectedOrphans, setSelectedOrphans] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch duplicates and test records
      const dupRes = await api.get('/students/duplicates');
      setDuplicateGroups(dupRes.data.duplicate_groups || []);
      setTestRecords(dupRes.data.test_records || []);
      
      // Fetch orphan bookings
      const orphanRes = await api.get('/data/orphan-bookings');
      setOrphanBookings(orphanRes.data.orphan_bookings || []);
      setOrphanEnrollments(orphanRes.data.orphan_enrollments || []);
    } catch (error) {
      toast.error('Failed to fetch cleanup data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Quick deduplicate - removes case-insensitive duplicates from bookings/enrollments
  const handleQuickDeduplicate = async () => {
    if (!confirm('This will automatically remove case-insensitive duplicate bookings and pending enrollments (e.g., "Evelin Pican" vs "EVELIN PICAN"). The most recent record will be kept. Continue?')) {
      return;
    }
    
    setProcessing(true);
    try {
      const res = await api.post('/bookings/deduplicate');
      setDedupeResult(res.data);
      
      if (res.data.total_duplicates_removed > 0) {
        toast.success(`Removed ${res.data.total_duplicates_removed} duplicate record(s)!`);
      } else {
        toast.info('No duplicates found - your data is clean!');
      }
      
      // Refresh other data too
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deduplicate');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  // Emergency fix for Evelin Pican
  const handleFixEvelin = async () => {
    if (!confirm('This will remove ALL duplicate Evelin Pican records, keeping only the PAID booking. Continue?')) {
      return;
    }
    
    setProcessing(true);
    try {
      const res = await api.post('/fix-evelin');
      
      if (res.data.total_deleted > 0) {
        toast.success(`Fixed! Deleted ${res.data.total_deleted} Evelin duplicate(s). Kept: ${res.data.kept_booking?.name || 'none'}`);
        alert(`Evelin Pican cleanup complete!\n\nDeleted:\n- ${res.data.details.bookings_deleted.length} booking(s)\n- ${res.data.details.pending_enrollments_deleted.length} pending enrollment(s)\n- ${res.data.details.pending_trial_enrollments_deleted.length} trial enrollment(s)\n- ${res.data.details.children_deleted.length} child record(s)\n\nKept booking: ${res.data.kept_booking?.name || 'none'} (${res.data.kept_booking?.status || 'n/a'})`);
      } else {
        toast.info('No Evelin duplicates found to fix');
      }
      
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix Evelin');
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSelectKeep = (groupIndex, studentId) => {
    setSelectedDuplicates(prev => ({
      ...prev,
      [groupIndex]: studentId
    }));
  };

  const handleMergeGroup = async (groupIndex) => {
    const group = duplicateGroups[groupIndex];
    const keepId = selectedDuplicates[groupIndex];
    
    if (!keepId) {
      toast.error('Please select which record to keep');
      return;
    }

    const deleteIds = group.students.filter(s => s.id !== keepId).map(s => s.id);
    
    if (deleteIds.length === 0) {
      toast.error('No records to merge');
      return;
    }

    setProcessing(true);
    try {
      const res = await api.post('/students/merge', {
        keep_id: keepId,
        delete_ids: deleteIds
      });
      
      toast.success(`Merged ${res.data.deleted_count} duplicate(s), transferred ${res.data.merged_bookings} booking(s)`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge students');
    } finally {
      setProcessing(false);
    }
  };

  const toggleTestSelect = (studentId) => {
    setSelectedTests(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllTests = () => {
    if (selectedTests.length === testRecords.length) {
      setSelectedTests([]);
    } else {
      setSelectedTests(testRecords.map(r => r.id));
    }
  };

  const handleDeleteTestRecords = async (permanent = false) => {
    if (selectedTests.length === 0) {
      toast.error('No test records selected');
      return;
    }

    const action = permanent ? 'permanently delete' : 'archive';
    if (!confirm(`Are you sure you want to ${action} ${selectedTests.length} test record(s)?`)) {
      return;
    }

    setProcessing(true);
    try {
      const res = await api.post('/students/bulk-delete', {
        student_ids: selectedTests,
        permanent: permanent
      });
      
      const count = permanent ? res.data.deleted_count : res.data.archived_count;
      toast.success(`${permanent ? 'Deleted' : 'Archived'} ${count} test record(s)`);
      setSelectedTests([]);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete records');
    } finally {
      setProcessing(false);
    }
  };

  // Orphan bookings handlers
  const toggleOrphanSelect = (bookingId) => {
    setSelectedOrphans(prev => 
      prev.includes(bookingId)
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const selectAllOrphans = () => {
    const allOrphanIds = [...orphanBookings.map(b => b.id), ...orphanEnrollments.map(e => e.id)];
    if (selectedOrphans.length === allOrphanIds.length) {
      setSelectedOrphans([]);
    } else {
      setSelectedOrphans(allOrphanIds);
    }
  };

  const handleDeleteOrphans = async () => {
    if (selectedOrphans.length === 0) {
      toast.error('No orphan records selected');
      return;
    }

    if (!confirm(`Are you sure you want to permanently delete ${selectedOrphans.length} orphan record(s)? This cannot be undone.`)) {
      return;
    }

    setProcessing(true);
    try {
      const bookingIds = orphanBookings.filter(b => selectedOrphans.includes(b.id)).map(b => b.id);
      const enrollmentIds = orphanEnrollments.filter(e => selectedOrphans.includes(e.id)).map(e => e.id);
      
      const res = await api.post('/data/delete-orphans', {
        booking_ids: bookingIds,
        enrollment_ids: enrollmentIds
      });
      
      toast.success(`Deleted ${res.data.deleted_bookings} booking(s) and ${res.data.deleted_enrollments} enrollment(s)`);
      setSelectedOrphans([]);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete orphan records');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
        <span className="ml-2 text-white/70">Analyzing data...</span>
      </div>
    );
  }

  const totalOrphans = orphanBookings.length + orphanEnrollments.length;

  return (
    <div className="space-y-6" data-testid="data-cleanup-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Data Cleanup Utility
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Identify and clean up duplicate, test, or orphan records
          </p>
        </div>
        <Button 
          onClick={fetchAllData} 
          variant="outline" 
          className="border-white/20 text-white hover:bg-white/10"
          disabled={processing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${processing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* EMERGENCY: Fix Evelin Pican */}
        <Card className="bg-gradient-to-br from-red-600/30 to-[#111] border-red-500">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mb-2" />
              <p className="text-sm text-white/60 mb-1">Emergency Fix</p>
              <p className="text-lg font-bold text-white mb-3">Evelin Pican</p>
              <Button
                onClick={handleFixEvelin}
                disabled={processing}
                variant="destructive"
                className="w-full"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Fix Now
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Deduplicate Card - CRITICAL */}
        <Card className="bg-gradient-to-br from-[#FF007F]/20 to-[#111] border-[#FF007F]/50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Zap className="w-8 h-8 text-[#FF007F] mb-2" />
              <p className="text-sm text-white/60 mb-1">Quick Fix</p>
              <p className="text-lg font-bold text-white mb-3">Case Duplicates</p>
              <Button
                onClick={handleQuickDeduplicate}
                disabled={processing}
                className="bg-[#FF007F] hover:bg-[#FF007F]/80 w-full"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Deduplicate Now
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#333]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/60">Duplicate Groups</p>
                <p className="text-3xl font-bold text-yellow-500">{duplicateGroups.length}</p>
              </div>
              <Users className="w-8 h-8 text-yellow-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/60">Test Records</p>
                <p className="text-3xl font-bold text-purple-500">{testRecords.length}</p>
              </div>
              <TestTube className="w-8 h-8 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#333] border-red-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/60">Orphan Records</p>
                <p className="text-3xl font-bold text-red-500">{totalOrphans}</p>
              </div>
              <Link2Off className="w-8 h-8 text-red-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deduplication Results */}
      {dedupeResult && dedupeResult.total_duplicates_removed > 0 && (
        <Card className="bg-green-500/10 border-green-500/50">
          <CardHeader>
            <CardTitle className="text-green-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Deduplication Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/60 text-sm">Bookings Cleaned</p>
                <p className="text-xl font-bold text-white">{dedupeResult.details.bookings.removed.length}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/60 text-sm">Pending Enrollments Cleaned</p>
                <p className="text-xl font-bold text-white">{dedupeResult.details.pending_enrollments.removed.length}</p>
              </div>
              <div className="bg-white/5 p-3 rounded-lg">
                <p className="text-white/60 text-sm">Imported Students Cleaned</p>
                <p className="text-xl font-bold text-white">{dedupeResult.details.imported_students.removed.length}</p>
              </div>
            </div>
            {dedupeResult.details.bookings.removed.length > 0 && (
              <div className="space-y-1">
                <p className="text-white/60 text-sm">Removed duplicates:</p>
                {dedupeResult.details.bookings.removed.map((r, i) => (
                  <p key={i} className="text-white/80 text-sm">
                    • {r.student} ({r.status})
                  </p>
                ))}
                {dedupeResult.details.pending_enrollments.removed.map((r, i) => (
                  <p key={`pe-${i}`} className="text-white/80 text-sm">
                    • {r.student} - £{r.amount?.toFixed(2)} (pending enrollment)
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Orphan Bookings Section - Show first as it's critical */}
      {totalOrphans > 0 && (
        <Card className="bg-[#111] border-red-500/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Link2Off className="w-5 h-5 text-red-500" />
                  Orphan Records ({totalOrphans})
                </CardTitle>
                <CardDescription className="text-white/50">
                  Bookings or enrollments that reference non-existent students. These cause data inconsistencies.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={selectAllOrphans}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {selectedOrphans.length === totalOrphans ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleDeleteOrphans}
                  disabled={selectedOrphans.length === 0 || processing}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected ({selectedOrphans.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {orphanBookings.map(booking => (
                <div 
                  key={booking.id}
                  onClick={() => toggleOrphanSelect(booking.id)}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                    selectedOrphans.includes(booking.id)
                      ? 'bg-red-500/20 border border-red-500/50'
                      : 'bg-white/5 border border-transparent hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedOrphans.includes(booking.id)}
                      className="border-white/30"
                    />
                    <div>
                      <p className="text-white font-medium">{booking.student_name}</p>
                      <p className="text-white/50 text-sm">{booking.class_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs text-red-400 border-red-400/50">
                      <Calendar className="w-3 h-3 mr-1" />
                      Orphan Booking
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={
                        booking.payment_status === 'paid' ? 'text-green-500 border-green-500/50' :
                        booking.status === 'pending' ? 'text-yellow-500 border-yellow-500/50' :
                        'text-white/50'
                      }
                    >
                      {booking.status}
                    </Badge>
                    {booking.amount > 0 && (
                      <span className="text-[#FF007F] font-mono text-sm">£{booking.amount.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              ))}
              
              {orphanEnrollments.map(enrollment => (
                <div 
                  key={enrollment.id}
                  onClick={() => toggleOrphanSelect(enrollment.id)}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                    selectedOrphans.includes(enrollment.id)
                      ? 'bg-red-500/20 border border-red-500/50'
                      : 'bg-white/5 border border-transparent hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedOrphans.includes(enrollment.id)}
                      className="border-white/30"
                    />
                    <div>
                      <p className="text-white font-medium">{enrollment.student_name}</p>
                      <p className="text-white/50 text-sm">Pending Enrollment</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs text-orange-400 border-orange-400/50">
                      Orphan Enrollment
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            
            {selectedOrphans.length > 0 && (
              <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-red-400 text-sm">
                  ⚠️ {selectedOrphans.length} orphan record(s) selected for deletion. This will permanently remove these records.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Duplicate Groups Section */}
      {duplicateGroups.length > 0 && (
        <Card className="bg-[#111] border-[#333]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Merge className="w-5 h-5 text-yellow-500" />
              Duplicate Students ({duplicateGroups.length} groups)
            </CardTitle>
            <CardDescription className="text-white/50">
              Select the record to keep for each group, then merge to combine bookings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {duplicateGroups.map((group, groupIndex) => (
              <div 
                key={groupIndex}
                className="border border-white/10 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <Badge variant="outline" className="text-yellow-500 border-yellow-500/50">
                      {group.type === 'same_name' ? 'Same Name' : 'Same Email & Name'}
                    </Badge>
                    <span className="ml-2 text-white font-medium">{group.key}</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMergeGroup(groupIndex)}
                    disabled={!selectedDuplicates[groupIndex] || processing}
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    <Merge className="w-4 h-4 mr-1" />
                    Merge
                  </Button>
                </div>
                
                <p className="text-xs text-white/50 italic">Click a row to select which record to KEEP, then click Merge. Or use the delete button to remove a specific record.</p>
                
                <div className="space-y-2">
                  {group.students.map(student => (
                    <div 
                      key={student.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                        selectedDuplicates[groupIndex] === student.id
                          ? 'bg-green-500/20 border border-green-500/50'
                          : 'bg-white/5 border border-transparent hover:bg-white/10'
                      }`}
                    >
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => handleSelectKeep(groupIndex, student.id)}
                      >
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          selectedDuplicates[groupIndex] === student.id
                            ? 'bg-green-500'
                            : 'bg-white/20'
                        }`}>
                          {selectedDuplicates[groupIndex] === student.id && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{student.name}</p>
                          <p className="text-white/50 text-sm">{student.parent_email || 'No email'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {student.source === 'children' ? 'Registered' : 'Imported'}
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className={
                            student.payment_status === 'paid' ? 'text-green-500 border-green-500/50' :
                            student.payment_status === 'pending' ? 'text-yellow-500 border-yellow-500/50' :
                            'text-white/50'
                          }
                        >
                          {student.payment_status}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${student.name}" (${student.source === 'children' ? 'Registered' : 'Imported'})? This cannot be undone.`)) {
                              setProcessing(true);
                              try {
                                await api.post('/students/bulk-delete', { student_ids: [student.id], permanent: true });
                                toast.success('Record deleted');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to delete');
                              } finally {
                                setProcessing(false);
                              }
                            }
                          }}
                          disabled={processing}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {selectedDuplicates[groupIndex] && (
                  <p className="text-sm text-green-400">
                    Will keep: "{group.students.find(s => s.id === selectedDuplicates[groupIndex])?.name}" 
                    and delete {group.students.length - 1} duplicate(s)
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Test Records Section */}
      {testRecords.length > 0 && (
        <Card className="bg-[#111] border-[#333]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <TestTube className="w-5 h-5 text-purple-500" />
                  Test Records ({testRecords.length})
                </CardTitle>
                <CardDescription className="text-white/50">
                  Records identified as test data (example.com emails, test names)
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={selectAllTests}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {selectedTests.length === testRecords.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDeleteTestRecords(false)}
                  disabled={selectedTests.length === 0 || processing}
                  className="bg-yellow-600 hover:bg-yellow-700"
                >
                  Archive Selected
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteTestRecords(true)}
                  disabled={selectedTests.length === 0 || processing}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Permanently
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {testRecords.map(record => (
                <div 
                  key={record.id}
                  onClick={() => toggleTestSelect(record.id)}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                    selectedTests.includes(record.id)
                      ? 'bg-purple-500/20 border border-purple-500/50'
                      : 'bg-white/5 border border-transparent hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      checked={selectedTests.includes(record.id)}
                      className="border-white/30"
                    />
                    <div>
                      <p className="text-white font-medium">{record.name}</p>
                      <p className="text-white/50 text-sm">{record.parent_email || 'No email'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs text-purple-400 border-purple-400/50">
                      {record.source === 'children' ? 'Registered' : 'Imported'}
                    </Badge>
                    <span className="text-white/50 text-xs">
                      {record.created_at ? new Date(record.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            {selectedTests.length > 0 && (
              <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <p className="text-purple-400 text-sm">
                  {selectedTests.length} record(s) selected for deletion
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* No Issues Found */}
      {duplicateGroups.length === 0 && testRecords.length === 0 && totalOrphans === 0 && (
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-white font-medium text-lg">Database is Clean!</h3>
            <p className="text-white/50 mt-2">
              No duplicate, test, or orphan records were found.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataCleanupTab;
